/**
 * PriceChart - Candlestick chart with SMA overlay
 */

import { useEffect, useRef, useState } from 'react';
import { createChart } from 'lightweight-charts';
import './PriceChart.css';

export default function PriceChart({ data, height = 300, onVisibleRangeChange }) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candlestickSeriesRef = useRef(null);
  const smaSeriesRef = useRef({});
  const [ohlcValues, setOhlcValues] = useState(null);

  useEffect(() => {
    if (!chartContainerRef.current || !data) return;

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height,
      layout: {
        background: { color: '#1a1a1a' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: '#2b2b2b' },
        horzLines: { color: '#2b2b2b' },
      },
      crosshair: {
        mode: 1, // Crosshair syncing
      },
      timeScale: {
        borderColor: '#2b2b2b',
        timeVisible: true,
        secondsVisible: false,
        barSpacing: 10,        // Wider candles for better visibility
        rightOffset: 5,        // Minimal right padding
        fixRightEdge: true,    // Prevent scrolling past latest bar
      },
      leftPriceScale: {
        borderColor: '#2b2b2b',
        autoScale: true,
        visible: true,
        scaleMargins: {
          top: 0.20,    // 20% padding for better zoom on candles
          bottom: 0.20,
        },
      },
      rightPriceScale: {
        visible: false,
      },
    });

    chartRef.current = chart;

    // Add candlestick series
    const candlestickSeries = chart.addCandlestickSeries({
      priceScaleId: 'left',
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: true,        // Enable borders for clarity
      borderUpColor: '#26a69a',   // Green border for bullish
      borderDownColor: '#ef5350', // Red border for bearish
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
      autoscaleInfoProvider: (original) => {
        const priceRange = original();
        if (priceRange !== null) {
          const range = priceRange.maxValue - priceRange.minValue;
          const minRange = 5; // Minimum $5 visible range (configurable)

          if (range < minRange) {
            const mid = (priceRange.maxValue + priceRange.minValue) / 2;
            return {
              priceRange: {
                minValue: mid - minRange / 2,
                maxValue: mid + minRange / 2
              }
            };
          }
        }
        return priceRange;
      }
    });
    candlestickSeriesRef.current = candlestickSeries;

    // Set candlestick data (filter out null entries from future date padding)
    if (data.ohlcv && data.ohlcv.length > 0) {
      const ohlcvData = data.ohlcv
        .filter(d => d.open != null && d.close != null)
        .map(d => ({
          time: d.date,
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close,
        }));
      candlestickSeries.setData(ohlcvData);
    }

    // Add SMA lines (only render available SMAs: 20, 200)
    if (data.sma) {
      const smaColors = {
        sma_20: 'rgba(255, 152, 0, 0.6)',   // Orange 60% opacity
        sma_200: 'rgba(244, 67, 54, 0.6)',  // Red 60% opacity
      };

      Object.keys(smaColors).forEach(smaKey => {
        if (data.sma[smaKey] && data.sma[smaKey].length > 0) {
          const smaSeries = chart.addLineSeries({
            priceScaleId: 'left',
            color: smaColors[smaKey],
            lineWidth: 2,
            title: smaKey.toUpperCase().replace('_', ' '),
          });

          const smaData = data.sma[smaKey].map(d => ({
            time: d.time,
            value: d.value,
          }));

          smaSeries.setData(smaData);
          smaSeriesRef.current[smaKey] = smaSeries;
        }
      });
    }

    // Subscribe to crosshair movement for value display
    chart.subscribeCrosshairMove((param) => {
      if (param.time && param.seriesData && param.seriesData.size > 0) {
        const candlestickData = param.seriesData.get(candlestickSeries);
        if (candlestickData) {
          setOhlcValues({
            open: candlestickData.open,
            high: candlestickData.high,
            low: candlestickData.low,
            close: candlestickData.close,
          });
        }
      } else if (data.ohlcv && data.ohlcv.length > 0) {
        // Show latest valid bar when no crosshair (filter out null padding)
        const validBars = data.ohlcv.filter(d => d.open != null && d.close != null);
        if (validBars.length > 0) {
          const latest = validBars[validBars.length - 1];
          setOhlcValues({
            open: latest.open,
            high: latest.high,
            low: latest.low,
            close: latest.close,
          });
        }
      }

      // Sync crosshair across all charts
      if (window.chartSync && chart) {
        window.chartSync.syncCrosshair(chart, param);
      }
    });

    // Initialize with latest valid values (filter out null padding)
    if (data.ohlcv && data.ohlcv.length > 0) {
      const validBars = data.ohlcv.filter(d => d.open != null && d.close != null);
      if (validBars.length > 0) {
        const latest = validBars[validBars.length - 1];
        setOhlcValues({
          open: latest.open,
          high: latest.high,
          low: latest.low,
          close: latest.close,
        });
      }
    }

    // Subscribe to visible range changes for syncing
    if (onVisibleRangeChange) {
      chart.timeScale().subscribeVisibleTimeRangeChange(onVisibleRangeChange);
    }

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [data, height, onVisibleRangeChange]);

  // Expose method for syncing visible range and register with chartSync
  useEffect(() => {
    if (chartRef.current && candlestickSeriesRef.current && window.chartSync) {
      window.chartSync.registerChart('price', chartRef.current, candlestickSeriesRef.current);
    }
  }, [chartRef.current, candlestickSeriesRef.current]);

  return (
    <div className="price-chart">
      <div className="chart-value-display">
        {ohlcValues && ohlcValues.open != null && (
          <span className="ohlc-values">
            O: {parseFloat(ohlcValues.open).toFixed(2)} H: {parseFloat(ohlcValues.high).toFixed(2)} L: {parseFloat(ohlcValues.low).toFixed(2)} C: {parseFloat(ohlcValues.close).toFixed(2)}
          </span>
        )}
        <div className="sma-legend">
          <span className="legend-item" style={{color: '#ff9800'}}>SMA 20</span>
          <span className="legend-item" style={{color: '#f44336'}}>SMA 200</span>
        </div>
      </div>
      <div ref={chartContainerRef} className="chart-container" />
    </div>
  );
}
